import { createParser, parseAsJson } from '@repo/sdk/qs/parsers'
import { z } from '@repo/schema'

import type { FilterModel, FilterOperators, FiltersState } from '../filters/core'
import type { SortingState } from '../table/tanstack-table'

const AmountFilterValueSchema = z.union([
  z.object({ amount: z.number().nullable(), currencies: z.array(z.string()) }),
  // Shared links and saved views from before several currencies hold one currency, or null.
  z
    .object({ amount: z.number().nullable(), currency: z.string().nullable() })
    .transform(({ amount, currency }) => ({
      amount,
      currencies: currency === null ? [] : [currency],
    })),
])

/* Filters as stored in the URL. Only amount values are checked, to read older ones. */
export const filtersSchema: z.ZodType<FiltersState> = z.array(
  z.union([
    z.object({
      columnId: z.string(),
      operator: z.custom<FilterOperators['amount']>(),
      type: z.literal('amount'),
      values: z.array(AmountFilterValueSchema),
    }),
    z.custom<FilterModel>(),
  ]),
)

export const dataTableFiltersParser = parseAsJson(filtersSchema.parse).withDefault([])

export const tableSortParser = createParser({
  parse: (query: string) => {
    const sorting: SortingState = []
    const params = new URLSearchParams(query)

    for (const [key, value] of params.entries()) {
      if (key.startsWith('sort[') && key.endsWith(']')) {
        const id = key.slice(5, -1)
        if (id && (value === 'asc' || value === 'desc')) {
          sorting.push({ desc: value === 'desc', id })
        }
      }
    }

    return sorting.length > 0 ? sorting : null
  },
  serialize: (sorting: null | SortingState) => {
    if (!sorting || sorting.length === 0) return ''

    const params = new URLSearchParams()
    sorting.forEach(({ desc, id }) => {
      params.set(`sort[${id}]`, desc ? 'desc' : 'asc')
    })

    return params.toString()
  },
})
